import { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Star, ShoppingCart, Loader2, SlidersHorizontal, X, ChevronDown, ChevronRight, ChevronUp,
  Flame, Shirt, CircleDot, Lamp, BookOpen, Landmark, Gift, Sparkles, Check
} from 'lucide-react';
import { toast } from 'sonner';
import '@/styles/shop.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category icon mapping
const categoryIcons = {
  'flame': Flame,
  'shirt': Shirt,
  'circle-dot': CircleDot,
  'lamp': Lamp,
  'book-open': BookOpen,
  'landmark': Landmark,
  'gift': Gift
};

// Category images
const categoryImages = {
  'cat_pooja_samagri': 'https://images.unsplash.com/photo-1729188986211-bc08ee4edf7d?w=80&h=80&fit=crop',
  'cat_mala_rudraksha': 'https://images.unsplash.com/photo-1764785314444-f6b6b4193bae?w=80&h=80&fit=crop',
  'cat_diyas_candles': 'https://images.unsplash.com/photo-1676354672676-8c150e31bfe1?w=80&h=80&fit=crop',
  'cat_spiritual_books': 'https://images.unsplash.com/photo-1767533427544-5f88d3fe4eed?w=80&h=80&fit=crop',
  'cat_idols_statues': 'https://images.unsplash.com/photo-1754666525419-e2aa1a0597d3?w=80&h=80&fit=crop',
  'cat_clothing': 'https://images.unsplash.com/photo-1770790038140-161ff68a42df?w=80&h=80&fit=crop',
  'cat_gifts': 'https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=80&h=80&fit=crop'
};

// Deity options
const deityOptions = [
  'Ganesha', 'Shiva', 'Vishnu', 'Lakshmi', 'Durga', 'Krishna', 'Hanuman', 'Saraswati'
];

// Occasion options
const occasionOptions = [
  'Daily Pooja', 'Diwali', 'Navratri', 'Ganesh Chaturthi', 'Wedding', 'Griha Pravesh', 'Satyanarayan'
];

// Product Card Component
const ProductCard = ({ product, onAddToCart, loading }) => {
  const hasDiscount = product.original_price && product.original_price > product.price;
  const discountPercent = hasDiscount 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;
  const savings = hasDiscount ? product.original_price - product.price : 0;

  return (
    <div 
      className="shop-product-card"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/shop/product/${product.id}`} className="block">
        <div className="shop-product-image-wrapper">
          <img
            src={product.images?.[0] || 'https://images.unsplash.com/photo-1633617127680-18229ebb1b94?w=300'}
            alt={product.name}
            className="shop-product-image"
          />
          
          {product.is_featured && (
            <span className="shop-product-badge">
              <Sparkles className="w-3 h-3" /> Featured
            </span>
          )}
          
          {hasDiscount && (
            <span className="shop-product-discount-badge">
              {discountPercent}% OFF
            </span>
          )}
        </div>
      </Link>
      
      <div className="shop-product-info">
        {/* Category */}
        <span className="shop-product-category">{product.category_name}</span>
        
        {/* Title - Fixed 2 lines with tooltip */}
        <Link to={`/shop/product/${product.id}`} title={product.name}>
          <h3 className="shop-product-name">{product.name}</h3>
        </Link>
        
        {/* Middle section - Rating & Savings */}
        <div className="shop-product-middle">
          {product.rating > 0 && (
            <div className="shop-product-rating">
              <span className="shop-product-rating-badge">
                <Star className="w-3 h-3 fill-current" />
                {product.rating}
              </span>
              <span className="shop-product-rating-count">({product.reviews_count})</span>
            </div>
          )}
          
          {hasDiscount && (
            <p className="shop-product-savings">Save ₹{savings}</p>
          )}
          
          {product.stock === 0 && (
            <p className="shop-product-stock">Out of stock</p>
          )}
        </div>
        
        {/* Footer - Price & Button always at bottom */}
        <div className="shop-product-footer">
          <div className="shop-product-price">
            <span className="shop-product-price-current">₹{product.price}</span>
            {hasDiscount && (
              <span className="shop-product-price-original">₹{product.original_price}</span>
            )}
          </div>
          
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddToCart(product.id);
            }}
            disabled={loading || product.stock === 0}
            className="shop-add-to-cart-btn"
            data-testid={`add-to-cart-${product.id}`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <ShoppingCart className="w-4 h-4" />
                Add to Cart
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Filter Section Component
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="shop-filter-section">
      <button 
        className="shop-filter-title w-full"
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && <div className="shop-filter-options">{children}</div>}
    </div>
  );
};

export default function CategoryPage() {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q');
  const navigate = useNavigate();
  const { user, refreshCart } = useOutletContext();
  
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedRating, setSelectedRating] = useState(null);
  const [showInStock, setShowInStock] = useState(false);
  const [selectedDeities, setSelectedDeities] = useState([]);
  const [selectedOccasions, setSelectedOccasions] = useState([]);

  const isAllProducts = categoryId === 'all';
  const isSearch = !categoryId && searchQuery;

  useEffect(() => {
    fetchData();
  }, [categoryId, searchQuery, sortBy]);

  useEffect(() => {
    applyFilters();
  }, [products, selectedCategories, priceRange, selectedRating, showInStock, selectedDeities, selectedOccasions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const categoriesRes = await axios.get(`${API}/shop/categories`);
      setCategories(categoriesRes.data);

      let params = { sort_by: sortBy, limit: 100 };
      
      if (categoryId && !isAllProducts) {
        params.category_id = categoryId;
        const cat = categoriesRes.data.find(c => c.id === categoryId);
        setCategory(cat);
      } else {
        setCategory(null);
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const productsRes = await axios.get(`${API}/shop/products`, { params });
      setProducts(productsRes.data);
      setFilteredProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...products];
    
    // Category filter (only for 'all' page)
    if (selectedCategories.length > 0 && isAllProducts) {
      result = result.filter(p => selectedCategories.includes(p.category_id));
    }
    
    // Price filter
    if (priceRange.min) {
      result = result.filter(p => p.price >= parseInt(priceRange.min));
    }
    if (priceRange.max) {
      result = result.filter(p => p.price <= parseInt(priceRange.max));
    }
    
    // Rating filter
    if (selectedRating) {
      result = result.filter(p => p.rating >= selectedRating);
    }
    
    // Stock filter
    if (showInStock) {
      result = result.filter(p => p.stock > 0);
    }
    
    setFilteredProducts(result);
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setPriceRange({ min: '', max: '' });
    setSelectedRating(null);
    setShowInStock(false);
    setSelectedDeities([]);
    setSelectedOccasions([]);
  };

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => 
      prev.includes(catId) 
        ? prev.filter(id => id !== catId)
        : [...prev, catId]
    );
  };

  const handleAddToCart = async (productId) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/auth');
      return;
    }

    setAddingToCart(productId);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/shop/cart`, 
        { product_id: productId, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Added to cart!');
      refreshCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const pageTitle = isSearch 
    ? `Search: "${searchQuery}"` 
    : isAllProducts 
      ? 'All Products' 
      : category?.name || 'Products';

  const hasActiveFilters = selectedCategories.length > 0 || priceRange.min || priceRange.max || selectedRating || showInStock || selectedDeities.length > 0 || selectedOccasions.length > 0;

  return (
    <div className="shop-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-3 px-4">
        <div className="shop-container flex items-center gap-2 text-sm">
          <Link to="/shop" className="text-gray-500 hover:text-amber-600 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <span className="text-gray-800 font-medium truncate">{pageTitle}</span>
        </div>
      </div>

      {/* Category Header Banner */}
      {(category || isAllProducts) && !isSearch && (
        <div 
          className="relative py-6 px-4 text-white"
          style={{
            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 50%, #CD853F 100%)',
          }}
        >
          <div className="shop-container relative z-10">
            <h1 className="text-2xl sm:text-3xl font-bold">{pageTitle}</h1>
            {category?.description && (
              <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">{category.description}</p>
            )}
            <p className="text-amber-200 text-sm mt-2">{filteredProducts.length} products found</p>
          </div>
          <span className="absolute right-8 top-1/2 -translate-y-1/2 text-6xl opacity-10 font-serif hidden lg:block">ॐ</span>
        </div>
      )}

      {/* Search Results Header */}
      {isSearch && (
        <div className="bg-white border-b border-gray-100 py-4 px-4">
          <div className="shop-container">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              Results for "{searchQuery}"
            </h1>
            <p className="text-gray-500 text-sm mt-1">{filteredProducts.length} products found</p>
          </div>
        </div>
      )}

      <div className="shop-container py-6">
        <div className="shop-layout-with-sidebar">
          {/* Filter Sidebar - Desktop Only */}
          <aside className="shop-filter-sidebar">
            <div className="shop-filter-card">
              <div className="shop-filter-header">
                <span>Filters</span>
                {hasActiveFilters && (
                  <button onClick={clearAllFilters} className="shop-filter-clear">
                    Clear All
                  </button>
                )}
              </div>
              
              {/* Category Filter (only show on "all" page) */}
              {isAllProducts && (
                <FilterSection title="Category">
                  {categories.map(cat => (
                    <label key={cat.id} className="shop-filter-option">
                      <input 
                        type="checkbox" 
                        checked={selectedCategories.includes(cat.id)}
                        onChange={() => toggleCategory(cat.id)}
                      />
                      <span>{cat.name}</span>
                      <span className="shop-filter-count">({cat.product_count})</span>
                    </label>
                  ))}
                </FilterSection>
              )}

              {/* Price Range Filter */}
              <FilterSection title="Price Range">
                <div className="shop-price-range">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                    className="shop-price-input"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                    className="shop-price-input"
                  />
                </div>
              </FilterSection>

              {/* Rating Filter */}
              <FilterSection title="Customer Rating">
                {[4, 3, 2, 1].map(rating => (
                  <label key={rating} className="shop-rating-option">
                    <input 
                      type="radio" 
                      name="rating"
                      checked={selectedRating === rating}
                      onChange={() => setSelectedRating(rating)}
                    />
                    <div className="shop-rating-stars">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star 
                          key={s} 
                          className={`shop-rating-star ${s <= rating ? '' : 'shop-rating-star-empty'}`} 
                          fill={s <= rating ? 'currentColor' : 'none'}
                        />
                      ))}
                    </div>
                    <span className="shop-rating-text">& Up</span>
                  </label>
                ))}
              </FilterSection>

              {/* Availability Filter */}
              <FilterSection title="Availability">
                <label className="shop-filter-option">
                  <input 
                    type="checkbox" 
                    checked={showInStock}
                    onChange={(e) => setShowInStock(e.target.checked)}
                  />
                  <span>In Stock Only</span>
                </label>
              </FilterSection>

              {/* Deity Filter */}
              <FilterSection title="Deity" defaultOpen={false}>
                {deityOptions.map(deity => (
                  <label key={deity} className="shop-filter-option">
                    <input 
                      type="checkbox" 
                      checked={selectedDeities.includes(deity)}
                      onChange={(e) => {
                        setSelectedDeities(prev => 
                          e.target.checked 
                            ? [...prev, deity]
                            : prev.filter(d => d !== deity)
                        );
                      }}
                    />
                    <span>{deity}</span>
                  </label>
                ))}
              </FilterSection>

              {/* Occasion Filter */}
              <FilterSection title="Occasion" defaultOpen={false}>
                {occasionOptions.map(occasion => (
                  <label key={occasion} className="shop-filter-option">
                    <input 
                      type="checkbox" 
                      checked={selectedOccasions.includes(occasion)}
                      onChange={(e) => {
                        setSelectedOccasions(prev => 
                          e.target.checked 
                            ? [...prev, occasion]
                            : prev.filter(o => o !== occasion)
                        );
                      }}
                    />
                    <span>{occasion}</span>
                  </label>
                ))}
              </FilterSection>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4 bg-white rounded-lg border border-gray-200 p-3">
              {/* Mobile Filter Button */}
              <button
                onClick={() => setShowFilters(true)}
                className="lg:hidden flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-amber-50 hover:text-amber-700 rounded-lg transition-colors"
                data-testid="mobile-filter-btn"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="text-sm font-medium">Filters</span>
                {hasActiveFilters && (
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                )}
              </button>

              <div className="hidden lg:block text-sm text-gray-500">
                Showing {filteredProducts.length} of {products.length} products
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 cursor-pointer"
                  data-testid="sort-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                  <option value="rating">Best Rating</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Active Filters Tags */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedCategories.map(catId => {
                  const cat = categories.find(c => c.id === catId);
                  return cat && (
                    <span key={catId} className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                      {cat.name}
                      <button onClick={() => toggleCategory(catId)} className="hover:text-amber-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
                {(priceRange.min || priceRange.max) && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                    ₹{priceRange.min || '0'} - ₹{priceRange.max || '∞'}
                    <button onClick={() => setPriceRange({ min: '', max: '' })} className="hover:text-amber-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedRating && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                    {selectedRating}+ Stars
                    <button onClick={() => setSelectedRating(null)} className="hover:text-amber-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {showInStock && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm rounded-full">
                    In Stock
                    <button onClick={() => setShowInStock(false)} className="hover:text-amber-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Products Grid */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Lamp className="w-10 h-10 text-amber-500 animate-pulse" />
                <p className="text-amber-700">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingCart className="w-10 h-10 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-6">Try adjusting your filters or search term</p>
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-full hover:bg-amber-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="shop-products-grid">
                {filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    loading={addingToCart === product.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between z-10">
              <h3 className="font-bold text-gray-800">Filters</h3>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button onClick={clearAllFilters} className="text-sm text-amber-600 font-medium">
                    Clear All
                  </button>
                )}
                <button 
                  onClick={() => setShowFilters(false)} 
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Category Filter */}
              {isAllProducts && (
                <FilterSection title="Category">
                  {categories.map(cat => (
                    <label key={cat.id} className="shop-filter-option">
                      <input 
                        type="checkbox" 
                        checked={selectedCategories.includes(cat.id)}
                        onChange={() => toggleCategory(cat.id)}
                      />
                      <span>{cat.name}</span>
                    </label>
                  ))}
                </FilterSection>
              )}

              {/* Price Range Filter */}
              <FilterSection title="Price Range">
                <div className="shop-price-range">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                    className="shop-price-input"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                    className="shop-price-input"
                  />
                </div>
              </FilterSection>

              {/* Rating Filter */}
              <FilterSection title="Customer Rating">
                {[4, 3, 2, 1].map(rating => (
                  <label key={rating} className="shop-rating-option">
                    <input 
                      type="radio" 
                      name="rating-mobile"
                      checked={selectedRating === rating}
                      onChange={() => setSelectedRating(rating)}
                    />
                    <div className="shop-rating-stars">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star 
                          key={s} 
                          className={`shop-rating-star ${s <= rating ? '' : 'shop-rating-star-empty'}`} 
                          fill={s <= rating ? 'currentColor' : 'none'}
                        />
                      ))}
                    </div>
                    <span className="shop-rating-text">& Up</span>
                  </label>
                ))}
              </FilterSection>

              {/* Availability Filter */}
              <FilterSection title="Availability">
                <label className="shop-filter-option">
                  <input 
                    type="checkbox" 
                    checked={showInStock}
                    onChange={(e) => setShowInStock(e.target.checked)}
                  />
                  <span>In Stock Only</span>
                </label>
              </FilterSection>

              {/* Deity Filter */}
              <FilterSection title="Deity" defaultOpen={false}>
                {deityOptions.map(deity => (
                  <label key={deity} className="shop-filter-option">
                    <input 
                      type="checkbox" 
                      checked={selectedDeities.includes(deity)}
                      onChange={(e) => {
                        setSelectedDeities(prev => 
                          e.target.checked 
                            ? [...prev, deity]
                            : prev.filter(d => d !== deity)
                        );
                      }}
                    />
                    <span>{deity}</span>
                  </label>
                ))}
              </FilterSection>

              {/* Occasion Filter */}
              <FilterSection title="Occasion" defaultOpen={false}>
                {occasionOptions.map(occasion => (
                  <label key={occasion} className="shop-filter-option">
                    <input 
                      type="checkbox" 
                      checked={selectedOccasions.includes(occasion)}
                      onChange={(e) => {
                        setSelectedOccasions(prev => 
                          e.target.checked 
                            ? [...prev, occasion]
                            : prev.filter(o => o !== occasion)
                        );
                      }}
                    />
                    <span>{occasion}</span>
                  </label>
                ))}
              </FilterSection>
            </div>

            {/* Apply Button */}
            <div className="sticky bottom-0 p-4 bg-white border-t border-gray-100">
              <button
                onClick={() => setShowFilters(false)}
                className="w-full py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
              >
                Apply Filters ({filteredProducts.length} products)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
